import type { AvailabilityFilters } from '@/stores/availability/availability-store';
import type { AppliedFilterChip } from '@/utils/inventory/filter-chip-labels';
import { formatMultiSelectValue } from '@/utils/filters/format-multi-select-value';

export interface AvailabilityFilterOptionSets {
  counterparties?: string[];
  caStatuses?: string[];
}

type FilterChipDef = {
  filterId: string;
  label: string;
  isActive: (f: AvailabilityFilters) => boolean;
  getValue: (f: AvailabilityFilters, opts?: AvailabilityFilterOptionSets) => string;
};

const CHIP_DEFS: FilterChipDef[] = [
  {
    filterId: 'quantity',
    label: 'Quantity',
    isActive: (f) => f.quantityMin !== null || f.quantityMax !== null,
    getValue: (f) => {
      if (f.quantityMin !== null && f.quantityMax !== null) return `${f.quantityMin}–${f.quantityMax}`;
      if (f.quantityMin !== null) return `≥ ${f.quantityMin}`;
      if (f.quantityMax !== null) return `≤ ${f.quantityMax}`;
      return '';
    },
  },
  {
    filterId: 'counterparties',
    label: 'Counterparty',
    isActive: (f) => f.counterparties.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.counterparties, opts?.counterparties),
  },
  {
    filterId: 'caStatuses',
    label: 'CA Status',
    isActive: (f) => f.caStatuses.length > 0,
    getValue: (f, opts) => formatMultiSelectValue(f.caStatuses, opts?.caStatuses),
  },
  {
    filterId: 'benchmarkFeeThreshold',
    label: 'Benchmark Fee',
    isActive: (f) => f.benchmarkFeeThreshold !== null && f.benchmarkFeeThreshold !== 0,
    getValue: (f) => `≥ ${f.benchmarkFeeThreshold}%`,
  },
  {
    filterId: 'excludeUpcomingCA',
    label: 'Exclude Upcoming CA',
    isActive: (f) => !f.excludeUpcomingCA,
    getValue: () => 'Off',
  },
  {
    filterId: 'hideISINNone',
    label: 'Hide ISIN None',
    isActive: (f) => !f.hideISINNone,
    getValue: () => 'Off',
  },
  {
    filterId: 'accountForClientTransfers',
    label: 'Client Transfers',
    isActive: (f) => f.accountForClientTransfers,
    getValue: () => 'On',
  },
];

export const getAppliedAvailabilityFilterChips = (
  filters: AvailabilityFilters,
  optionSets?: AvailabilityFilterOptionSets
): AppliedFilterChip[] => {
  const chips: AppliedFilterChip[] = [];
  for (const def of CHIP_DEFS) {
    if (!def.isActive(filters)) continue;
    const value = def.getValue(filters, optionSets);
    chips.push({
      filterId: def.filterId,
      label: value ? `${def.label}: ${value}` : def.label,
    });
  }
  return chips;
};
